import { useMemo, useState, memo } from "react";
import { Link } from "react-router-dom";
import { routes } from "routes";
import { IGif, IUser } from "services/types";
import { CurrentUserObject } from "hooks/useVisitorInfo";
import { ReactComponent as PlusIcon } from "components/icons/plusIcon.svg";
import { ReactComponent as CheckIcon } from "components/icons/tick.svg";
import { ReactComponent as DefaultUserIcon } from "components/icons/default_userpic.svg";
import VerifiedUserIcon from "components/UI/VerifiedUserIcon";
import "./UserInfo.scss";

interface IUserInfoProps {
  gif: IGif;
  user: IUser;
  rel?: string;
  followingIds: string[];
  currentUser: CurrentUserObject | null;
  onProfileMetric: (value: string) => void;
  onFollow: () => void;
}

const TIMESTAMP_TO_TIME = 1000;
const MAX_LENGTH = 40;

export const UserInfo: React.FC<IUserInfoProps> = memo(({
  gif,
  user,
  currentUser,
  rel,
  followingIds,
  onFollow,
  onProfileMetric
}) => {
  const [showMore, setShowMore] = useState(false);
  const following = useMemo(() => followingIds.indexOf(user.username) >= 0, [followingIds, user]);
  const isCurrentUserGif = currentUser?.username === user.username;
  const createDate = new Date(gif.createDate * TIMESTAMP_TO_TIME)
    .toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  const gifDescription = useMemo(() => gif.description || user.description, [user, gif]);
  const isShowMore = gifDescription?.length < MAX_LENGTH;

  const handleUserClick = () => {
    onProfileMetric(user.username);
  };

  return (
    <div className="UserInfo">
      <div className="UserInfo-ImageWrap">
        <Link
          to={routes.user(user.username)}
          rel="noopener noreferrer"
          onClick={handleUserClick}
        >
          {user.profileImageUrl ? <img loading="lazy" src={user.profileImageUrl} alt=""/> : <DefaultUserIcon />}
        </Link>
      </div>
      {user.verified && <div className="UserInfo-VerifyMark"><CheckIcon /></div>}
      {!isCurrentUserGif && (
        <button className="UserInfo-FollowBtn" title={user.username} onClick={onFollow} disabled={following}>
          {following ? <CheckIcon /> : <PlusIcon />}
        </button>
      )}
      <div className="UserInfo-Text">
        <Link className="UserInfo-Date" to={routes.watch(gif.id, rel)} rel="noopener noreferrer">{createDate}</Link>
        <Link className="UserInfo-UserLink" to={routes.user(user.username)} rel="noopener noreferrer" onClick={handleUserClick}>
          {user.name || user.username}
          {user.verified && <VerifiedUserIcon />}
        </Link>
      </div>
      {gifDescription && (
        <p className="UserInfo-Description">
          {isShowMore || showMore ? gifDescription : `${gifDescription?.substring(0, MAX_LENGTH)}...`}
          {" "}
          {!isShowMore && <button onClick={() => setShowMore(prev => !prev)}>{showMore ? "Less" : " More"}</button>}
        </p>
      )}
    </div>
  );
});
